/**
 * Shopify Wishlist — wishlist.js
 * Drop into: assets/wishlist.js
 * Include in theme.liquid before </body>:
 *   {{ 'wishlist.js' | asset_url | script_tag }}
 *
 * Stores product data in localStorage under 'shopify_wishlist'.
 * Dispatches custom events: wishlist:added, wishlist:removed, wishlist:changed
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'shopify_wishlist';

  /* ─────────────── CORE API ─────────────── */

  const Wishlist = {
    get() {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
      catch { return []; }
    },

    save(items) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { items } }));
    },

    has(id) {
      return this.get().some(p => String(p.id) === String(id));
    },

    add(product) {
      /* product: { id, handle, title, price, image, vendor, tag } */
      if (this.has(product.id)) return false;
      const list = this.get();
      list.push(product);
      this.save(list);
      document.dispatchEvent(new CustomEvent('wishlist:added', { detail: { product } }));
      return true;
    },

    remove(id) {
      const list = this.get().filter(p => String(p.id) !== String(id));
      this.save(list);
      document.dispatchEvent(new CustomEvent('wishlist:removed', { detail: { id } }));
    },

    toggle(product) {
      return this.has(product.id) ? (this.remove(product.id), false) : (this.add(product), true);
    },

    clear() {
      this.save([]);
    },

    count() {
      return this.get().length;
    }
  };

  /* ─────────────── DRAWER HTML ─────────────── */

  function buildDrawer() {
    const el = document.createElement('div');
    el.innerHTML = `
      <div id="wl-overlay" style="
        position:fixed;inset:0;background:rgba(10,10,10,0);z-index:990;
        visibility:hidden;transition:background .35s,visibility .35s;"></div>

      <aside id="wl-drawer" aria-label="Wishlist" style="
        position:fixed;top:0;right:0;width:420px;max-width:100vw;height:100vh;
        background:#fafafa;border-left:1px solid #e0e0e0;display:flex;
        flex-direction:column;transform:translateX(100%);
        transition:transform .45s cubic-bezier(.16,1,.3,1);z-index:991;overflow:hidden;
        ">

        <div style="padding:28px 32px 24px;border-bottom:1px solid #e0e0e0;
          display:flex;align-items:flex-start;justify-content:space-between;flex-shrink:0;">
          <div>
            <div style="font-size:26px;font-weight:300;letter-spacing:.02em;
              line-height:1;font-family: var(--font-heading-family);text-transform:uppercase;">Wishlist</div>
            <div id="wl-subtitle" style="font-size:11px;letter-spacing:.1em;
              text-transform:uppercase;color:#000;margin-top:6px;">0 items saved</div>
          </div>
          <button id="wl-close" aria-label="Close wishlist" style="
            background:none;border:none;cursor:pointer;padding:4px;color:#555;
            display:flex;align-items:center;margin-top:2px;transition:transform .2s;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="1.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div id="wl-items" style="flex:1;overflow-y:auto;padding:8px 0;"></div>

        <div id="wl-footer" style="display:none;padding:24px 32px;
          border-top:1px solid #e0e0e0;flex-shrink:0;">
          <button id="wl-add-all" style="
            display:none;width:100%;padding:14px 24px;background:#0a0a0a;
            color:#fafafa;border:none;font-size:12px;font-weight:500;
            letter-spacing:.12em;text-transform:uppercase;cursor:pointer;">
            Add All to Cart
          </button>
          <button id="wl-clear" style="
            display:block;width:100%;padding:12px 24px;background:transparent;
            color:#555;border:1px solid #000;font-size:12px;font-weight:400;
            letter-spacing:.1em;text-transform:uppercase;cursor:pointer;margin-top:10px;text-align:center;">
            Clear Wishlist
          </button>
          <div id="wl-footer-count" style="font-size:11px;color:#999;
            text-align:center;margin-top:14px;letter-spacing:.06em;display:none"></div>
        </div>
      </aside>`;
    document.body.appendChild(el.firstElementChild); /* overlay */
    document.body.appendChild(el.lastElementChild);  /* drawer  */
  }

  /* ─────────────── RENDER ─────────────── */

  function renderItems() {
    const list = Wishlist.get();
    const container = document.getElementById('wl-items');
    if (!container) return;

    if (list.length === 0) {
      container.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;
          justify-content:center;padding:64px 32px;text-align:center;gap:16px;">
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none"
            stroke="#e0e0e0" stroke-width="1">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06
              a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06
              a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <div style="font-size:22px;font-weight:400;
            color:#0a0a0a;">Nothing saved yet</div>
          <div style="font-size:13px;color:#999;line-height:1.7;max-width:240px;">
            Tap the heart icon on any product to save it for later.</div>
        </div>`;
      return;
    }

    container.innerHTML = list.map((p, i) => `
      <div style="display:flex;align-items:center;gap:16px;padding:20px 32px;
        border-bottom:1px solid #e0e0e0;animation:wlSlideIn .3s both;
        animation-delay:${i * 40}ms;">
        <a href="/products/${p.handle}" style="display:block;flex-shrink:0;">
          <img src="${p.image}" alt="${p.title}" loading="lazy" style="
            width:72px;height:90px;object-fit:cover;background:#f2f2f2;
            filter:grayscale(100%);display:block;">
        </a>
        <div style="flex:1;min-width:0;">
          <div style="font-size:15px;font-weight:500;
            text-transform:uppercase;color:#000;margin-bottom:4px;    font-family: var(--font-heading-family);">
            ${p.vendor}${p.tag ? ' · ' + p.tag : ''}</div> 
          <div style="font-size:13px;color:#555;">${p.price}</div>
        </div>
        <button data-wl-remove="${p.id}" aria-label="Remove ${p.title}" style="
          background:none;border:1px solid transparent;cursor:pointer;padding:6px;
          color:#000;display:flex;align-items:center;border-radius:50%;flex-shrink:0;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="1.5">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>`).join('');
  }

  function updateUI() {
    const count = Wishlist.count();

    /* badge */
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });

    /* subtitle + footer */
    const subtitle = document.getElementById('wl-subtitle');
    if (subtitle) subtitle.textContent = count === 1 ? '1 item saved' : `${count} items saved`;

    const footer = document.getElementById('wl-footer');
    if (footer) footer.style.display = count > 0 ? 'none' : 'none';

    const fc = document.getElementById('wl-footer-count');
    if (fc) fc.textContent = count > 0
      ? `${count} piece${count !== 1 ? 's' : ''} in your wishlist` : '';

    /* sync all heart buttons */
    document.querySelectorAll('[data-wishlist-id]').forEach(btn => {
      const id = btn.dataset.wishlistId;
      const active = Wishlist.has(id);
      btn.classList.toggle('wl-active', active);
      btn.setAttribute('aria-pressed', active);
    });
  }

  /* ─────────────── DRAWER OPEN/CLOSE ─────────────── */

  function openDrawer() {
    renderItems();
    const drawer = document.getElementById('wl-drawer');
    const overlay = document.getElementById('wl-overlay');
    if (drawer) drawer.style.transform = 'translateX(0)';
    if (overlay) { overlay.style.background = 'rgba(10,10,10,.5)'; overlay.style.visibility = 'visible'; }
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    const drawer = document.getElementById('wl-drawer');
    const overlay = document.getElementById('wl-overlay');
    if (drawer) drawer.style.transform = 'translateX(100%)';
    if (overlay) { overlay.style.background = 'rgba(10,10,10,0)'; overlay.style.visibility = 'hidden'; }
    document.body.style.overflow = '';
  }

  /* ─────────────── EVENT DELEGATION ─────────────── */

  document.addEventListener('click', function (e) {
    /* open drawer */
    if (e.target.closest('[data-wishlist-trigger]')) {
      e.preventDefault();
      openDrawer();
    }

    /* close drawer */
    if (e.target.closest('#wl-close') || e.target.id === 'wl-overlay') {
      closeDrawer();
    }

    /* toggle wishlist from product cards */
    const wishBtn = e.target.closest('[data-wishlist-id]');
    if (wishBtn) {
      e.preventDefault();
      const id = wishBtn.dataset.wishlistId;
      const product = {
        id,
        handle:  wishBtn.dataset.wishlistHandle  || '',
        title:   wishBtn.dataset.wishlistTitle   || '',
        price:   wishBtn.dataset.wishlistPrice   || '',
        image:   wishBtn.dataset.wishlistImage   || '',
        vendor:  wishBtn.dataset.wishlistVendor  || '',
        tag:     wishBtn.dataset.wishlistTag     || '',
      };
      const added = Wishlist.toggle(product);
      wishBtn.classList.toggle('wl-active', added);
      renderItems();
      updateUI();
      showToast(added ? `Product added to wishlist` : 'Removed from wishlist');
    }

    /* remove from drawer */
    const removeBtn = e.target.closest('[data-wl-remove]');
    if (removeBtn) {
      Wishlist.remove(removeBtn.dataset.wlRemove);
      renderItems();
      updateUI();
    }

    /* clear all */
    if (e.target.id === 'wl-clear') {
      Wishlist.clear();
      renderItems();
      updateUI();
    }

    /* add all to cart */
    if (e.target.id === 'wl-add-all') {
      addAllToCart();
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ─────────────── ADD ALL TO CART ─────────────── */

  async function addAllToCart() {
    const items = Wishlist.get().map(p => ({ id: p.id, quantity: 1 }));
    if (!items.length) return;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      showToast(`${items.length} item${items.length !== 1 ? 's' : ''} added to cart`);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch {
      showToast('Could not add items — please try again');
    }
  }

  /* ─────────────── TOAST ─────────────── */

  function showToast(msg) {
    let tc = document.getElementById('wl-toast-container');
    if (!tc) {
      tc = document.createElement('div');
      tc.id = 'wl-toast-container';
      tc.style.cssText = 'position:fixed;bottom:32px;left:50%;transform:translateX(-50%);z-index:9999;display:flex;flex-direction:column;gap:8px;align-items:center;pointer-events:none;';
      document.body.appendChild(tc);
    }
    const t = document.createElement('div');
    t.textContent = msg;
    t.style.cssText = `background:#0a0a0a;color:#fafafa;padding:12px 20px;
      font-family:'DM Sans','Helvetica Neue',sans-serif;font-size:12px;
      letter-spacing:.06em;white-space:nowrap;
      animation:wlToastIn .3s cubic-bezier(.16,1,.3,1) both,
                wlToastOut .3s cubic-bezier(.16,1,.3,1) 2.2s both;`;
    tc.appendChild(t);
    setTimeout(() => t.remove(), 2600);
  }

  /* ─────────────── INJECT STYLES ─────────────── */

  const style = document.createElement('style');
  style.textContent = `
    @keyframes wlSlideIn { from{opacity:0;transform:translateX(12px)} to{opacity:1;transform:none} }
    @keyframes wlToastIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
    @keyframes wlToastOut { from{opacity:1;transform:none} to{opacity:0;transform:translateY(8px)} }
    [data-wishlist-id] svg path { stroke:#0a0a0a;fill:none;stroke-width:1.5;transition:fill .2s,stroke .2s; }
    [data-wishlist-id].wl-active svg path { fill:#0a0a0a; }
    #wl-close:hover { transform:rotate(90deg); }
  `;
  document.head.appendChild(style);

  /* ─────────────── INIT ─────────────── */

  function init() {
    buildDrawer();
    updateUI();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* expose globally for Liquid templates */
  window.ShopifyWishlist = Wishlist;

})();
